import { EventScoreboard, FootballStats, FootballStatsEntry, PairScore } from '@frontend/sports/betting-offer/feature/model';

import { PairScoreboardViewModel } from './pair-scoreboard.viewmodel';
import { PairGameScoreboardStats, ScoreboardStat } from './scoreboard-stats';

export class SoccerScoreboardViewModel extends PairScoreboardViewModel {
    private redCardsStats = 'redCards';
    private yellowCardsStats = 'yellowCards';
    private cornersStats = 'corners';
    private penaltiesStats = 'penalties';
    override stats: PairGameScoreboardStats;
    isExpanded: boolean;

    constructor() {
        super();
        this.stats = new PairGameScoreboardStats();
        this.isExpanded = false;
    }

    private __fillSoccerStats(scoreboard: EventScoreboard): void {
        const stats = scoreboard.statistics as FootballStats;

        this.stats.updateStatValue(this.scoreKey, { icon: 'sports-sb-goal-f' });

        if (!stats) {
            this.stats.setStats(this.redCardsStats, 1, undefined, 'sports-sb-card', true, 'red');
            this.stats.setStats(this.yellowCardsStats, 1, undefined, 'sports-sb-card', true, 'yellow');
            this.stats.setStats(this.cornersStats, 1, undefined, 'sports-sb-corner');
            this.stats.setStats(this.penaltiesStats, 1, undefined, 'sports-sb-penalty-f');

            return;
        }

        this.stats.setStats(this.redCardsStats, 1, this.getStatsValue(stats.redcards), 'sports-sb-card', this.isExpanded, 'red');
        this.stats.setStats(this.yellowCardsStats, 2, this.getStatsValue(stats.yellowcards), 'sports-sb-card', this.isExpanded, 'yellow');
        this.stats.setStats(this.cornersStats, 3, this.getStatsValue(stats.corners), 'sports-sb-corner', this.isExpanded);
        this.stats.setStats(this.penaltiesStats, 4, this.getStatsValue(stats.penalties), 'sports-sb-penalty-f', this.isExpanded);
    }

    private getStatsValue(entry?: FootballStatsEntry): PairScore<number> | undefined {
        if (!entry) {
            return undefined;
        }

        const periods = [];
        if (entry.regularTime) {
            periods.push(entry.regularTime);
            periods.push(entry.extraTime);
        } else {
            periods.push(entry.firstHalf);
            periods.push(entry.secondHalf);
        }

        return periods
            .filter((p) => !!p)
            .reduce(
                (acc: PairScore<number>, p) => {
                    acc.participant1 += p!.participant1;
                    acc.participant2 += p!.participant2;

                    return acc;
                },
                { participant1: 0, participant2: 0 },
            );
    }

    private __updateSoccerStats(scoreboard: EventScoreboard): void {
        const stats = scoreboard.statistics as FootballStats;
        if (!stats) {
            return;
        }

        this.stats.updateStatValueFromDoubleCounter(this.redCardsStats, this.getStatsValue(stats.redcards));
        this.stats.updateStatValueFromDoubleCounter(this.yellowCardsStats, this.getStatsValue(stats.yellowcards));
        this.stats.updateStatValueFromDoubleCounter(this.cornersStats, this.getStatsValue(stats.corners));
        this.stats.updateStatValueFromDoubleCounter(this.penaltiesStats, this.getStatsValue(stats.penalties));
    }

    setIsExpanded(isExpanded: boolean): void {
        this.stats.updateStatVisibility(this.redCardsStats, isExpanded);
        this.stats.updateStatVisibility(this.yellowCardsStats, isExpanded);
        this.stats.updateStatVisibility(this.cornersStats, isExpanded);
        this.stats.updateStatVisibility(this.penaltiesStats, isExpanded);
        this.isExpanded = isExpanded;
    }

    protected override fillFromScoreboard(scoreboard: EventScoreboard): void {
        super.fillFromScoreboard(scoreboard);
        this.__fillSoccerStats(scoreboard);
    }

    override updateFromScoreboard(scoreboard: EventScoreboard): void {
        super.updateFromScoreboard(scoreboard);
        this.__updateSoccerStats(scoreboard);
    }

    override get score(): ScoreboardStat {
        return this.stats.values[this.scoreKey];
    }

    get redCards(): ScoreboardStat {
        return this.stats.values[this.redCardsStats];
    }

    get yellowCards(): ScoreboardStat {
        return this.stats.values[this.yellowCardsStats];
    }

    get corners(): ScoreboardStat {
        return this.stats.values[this.cornersStats];
    }

    get penalties(): ScoreboardStat {
        return this.stats.values[this.penaltiesStats];
    }
}
